import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, ShieldCheck, BadgeCheck, Phone, MessageCircle, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import useSeo from '../hooks/useSeo';
import { SiteHeader, SiteFooter } from './ServicesIndex';
import StickyMobileCTA from '../components/sections/StickyMobileCTA';
import { REVIEWS, SERVICE_FILTERS, REVIEW_STATS } from '../data/reviewsData';

const PAGE_SIZE = 12;

export default function TestimonialsReviews({ onOpenCalculator }) {
  useSeo(
    'Customer Reviews',
    'Read 2,867+ verified customer reviews for EntoRid — the most trusted pest control service in Dehradun, Haridwar, Rishikesh and across Uttarakhand.'
  );

  const [filter, setFilter] = useState('all');
  const [visible, setVisible] = useState(PAGE_SIZE);

  const filtered = useMemo(
    () => (filter === 'all' ? REVIEWS : REVIEWS.filter(r => r.service === filter)),
    [filter]
  );

  const shown = filtered.slice(0, visible);

  const changeFilter = (k) => {
    setFilter(k);
    setVisible(PAGE_SIZE);
  };

  const starDistribution = useMemo(() => {
    const dist = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    filtered.forEach(r => { dist[r.rating] = (dist[r.rating] || 0) + 1; });
    return dist;
  }, [filtered]);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
      <SiteHeader onOpenCalculator={onOpenCalculator} />

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-4 text-sm text-gray-500" data-testid="breadcrumb">
          <Link to="/" className="hover:text-brand-green">Home</Link>
          <span className="mx-2">›</span>
          <span className="text-gray-500">Testimonials</span>
          <span className="mx-2">›</span>
          <span className="text-gray-700 font-medium">Customer Reviews</span>
        </div>
      </div>

      {/* Hero — trust elements */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-blue via-brand-blue-light to-brand-blue-dark text-white">
        <div aria-hidden className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-green/30 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 md:py-20">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 px-3.5 py-1.5 text-xs sm:text-sm font-medium">
                <Sparkles size={14} className="text-brand-green-light" /> 100% Genuine Reviews
              </span>
              <h1 className="mt-5 text-3xl sm:text-5xl font-bold tracking-tight" data-testid="reviews-title">
                Loved by <span className="text-brand-green-light">{REVIEW_STATS.totalCustomers}</span> Customers
              </h1>
              <p className="mt-4 text-base sm:text-lg text-white/80 max-w-xl">
                Real stories from real families across Uttarakhand. Every review is verified and unedited.
              </p>

              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/85">
                <span className="inline-flex items-center gap-2"><BadgeCheck size={15} className="text-brand-green-light" /> Verified Customers</span>
                <span className="inline-flex items-center gap-2"><ShieldCheck size={15} className="text-brand-green-light" /> Govt Approved</span>
              </div>

              <div className="mt-7 flex gap-3">
                <Link
                  to="/testimonials/videos"
                  data-testid="switch-to-videos"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white/10 text-white border border-white/25 hover:bg-white/20 font-semibold text-sm transition-colors"
                >
                  Watch Video Testimonials <ArrowRight size={16} />
                </Link>
              </div>
            </div>

            {/* Rating summary card */}
            <div className="bg-white text-brand-blue rounded-3xl p-7 shadow-2xl" data-testid="rating-summary">
              <div className="flex items-center gap-4 mb-6">
                <div className="text-5xl sm:text-6xl font-bold text-brand-green">{REVIEW_STATS.averageRating}</div>
                <div>
                  <div className="flex gap-1 mb-1" aria-label={`${REVIEW_STATS.averageRating} out of 5 stars`}>
                    {[1, 2, 3, 4, 5].map(i => (
                      <Star key={i} size={20} className="fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <div className="text-sm text-gray-500" data-testid="reviews-count-line">Based on {REVIEWS.length.toLocaleString('en-IN')} reviews</div>
                </div>
              </div>

              <div className="space-y-2">
                {[5, 4, 3, 2, 1].map(rating => {
                  const count = REVIEWS.filter(r => r.rating === rating).length;
                  const pct = (count / REVIEWS.length) * 100;
                  return (
                    <div key={rating} className="flex items-center gap-3 text-sm">
                      <span className="w-3 text-right text-gray-600">{rating}</span>
                      <Star size={13} className="fill-amber-400 text-amber-400" />
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-14 text-right text-gray-500 text-xs tabular-nums">{count.toLocaleString('en-IN')}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-[132px] sm:top-[120px] z-20 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-4 overflow-x-auto">
          <div className="flex gap-2 min-w-max" data-testid="review-filters">
            {SERVICE_FILTERS.map(f => (
              <button
                key={f.key}
                type="button"
                onClick={() => changeFilter(f.key)}
                data-testid={`filter-${f.key}`}
                className={`h-9 px-4 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  filter === f.key
                    ? 'bg-brand-green text-white shadow-md'
                    : 'bg-slate-100 text-gray-700 hover:bg-slate-200'
                }`}
              >
                {f.label}
                {filter === f.key && (
                  <span className="ml-1.5 opacity-70">({filtered.length})</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews grid */}
      <main className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="reviews-grid">
          {shown.map(review => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>

        {visible < filtered.length && (
          <div className="mt-10 text-center">
            <Button
              onClick={() => setVisible(v => v + PAGE_SIZE)}
              data-testid="load-more-btn"
              className="h-12 px-8 rounded-full bg-brand-blue hover:bg-brand-blue-light text-white font-semibold"
            >
              Load More Reviews ({filtered.length - visible} remaining)
            </Button>
          </div>
        )}

        {filtered.length === 0 && (
          <div className="py-16 text-center text-gray-500" data-testid="no-reviews">
            <p>No reviews yet for this service.</p>
          </div>
        )}
      </main>

      {/* Final CTA */}
      <section className="relative overflow-hidden py-14 sm:py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-blue via-brand-blue-light to-brand-green" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-2xl sm:text-4xl font-bold leading-tight" data-testid="reviews-final-cta">
            Join {REVIEW_STATS.totalCustomers} Happy Customers
          </h2>
          <p className="mt-3 text-base sm:text-lg text-white/85 max-w-xl mx-auto">
            Experience the most trusted pest control service in Uttarakhand.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button onClick={onOpenCalculator} data-testid="reviews-book-btn" className="h-12 px-7 rounded-full bg-brand-green hover:bg-brand-green-dark text-white font-semibold shadow-lg">
              Book Now
            </Button>
            <Button onClick={onOpenCalculator} variant="outline" data-testid="reviews-inspection-btn" className="h-12 px-7 rounded-full bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white font-semibold">
              Get Free Inspection
            </Button>
            <a href="https://api.whatsapp.com/send?phone=919410110910" target="_blank" rel="noreferrer" data-testid="reviews-wa-btn" className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white text-brand-blue hover:bg-white/95 font-semibold shadow-lg">
              <MessageCircle size={18} /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function ReviewCard({ review }) {
  return (
    <article
      data-testid={`review-${review.id}`}
      className="bg-white rounded-2xl p-5 sm:p-6 ring-1 ring-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
    >
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-11 h-11 rounded-full text-white font-semibold flex items-center justify-center text-sm shrink-0"
          style={{ backgroundColor: review.avatarColor }}
          aria-hidden
        >
          {review.initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-brand-blue text-sm truncate">{review.name}</div>
          <div className="text-xs text-gray-500 flex items-center gap-1 truncate">
            <MapPin size={11} /> {review.city}
          </div>
        </div>
        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold uppercase tracking-wider">
          <BadgeCheck size={10} /> Verified
        </div>
      </div>

      <div className="flex items-center justify-between mb-2.5">
        <div className="flex gap-0.5" aria-label={`${review.rating} out of 5 stars`}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              size={14}
              className={i < review.rating ? 'fill-amber-400 text-amber-400' : 'fill-gray-200 text-gray-200'}
            />
          ))}
        </div>
        <span className="text-xs text-gray-400">{review.daysAgo}d ago</span>
      </div>

      <p className="text-sm text-gray-700 leading-relaxed mb-3">"{review.text}"</p>

      <div className="pt-3 border-t border-gray-100 text-xs text-brand-green font-medium">
        {review.serviceLabel}
      </div>
    </article>
  );
}
